/* eslint-disable no-restricted-syntax */
/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
 GridCallbackDetails,
 GridFilterItem,
 GridFilterModel,
 GridSortModel,
} from '@mui/x-data-grid-premium';
import dayjs from 'dayjs';
import { Dispatch, SetStateAction } from 'react';

import { StructureType } from '../../../types/types';
import { findStructure, isJson } from '../../../utils/dataTypes';

export const filterMap = (filters: any): GridFilterModel => ({
 items:
  typeof filters !== 'undefined' && Array.isArray(filters)
   ? filters?.map((item: any, i: any) => ({
      ...item,
      id: i,
      operatorValue: item?.operatorValue || 'equals',
     }))
   : [],
});

type TParseToInt = (param: string | string[]) => number;

export const parseToInt: TParseToInt = (param) => {
 if (Array.isArray(param)) {
  return parseInt(param[0], 10) || 0;
 }

 return parseInt(param, 10) || 0;
};

type TDecodeURI = (param: string | string[]) => string | string[];

export const decodeURI: TDecodeURI = (param) => {
 if (Array.isArray(param)) {
  return param.map((item) => decodeURIComponent(item) || '');
 }

 return decodeURIComponent(param) || '';
};

export const lowercaseKeys = (obj: Array<any>): any => {
 if (Array.isArray(obj)) {
  return obj.map(lowercaseKeys);
 }
 if (obj && typeof obj === 'object') {
  const newObj = {};
  for (const key in obj as any) {
   if ((obj as any)?.hasOwnProperty(key)) {
    (newObj as any)[key.toLowerCase()] = lowercaseKeys(obj[key]);
   }
  }
  return newObj;
 }
 return obj; // Return the value if it's neither an object nor an array
};

export const constructRows = (rows: any) => {
 const hasId =
  rows?.[0]?.id &&
  (typeof rows[0].id === 'string' || typeof rows[0].id === 'number');
 const hasNo =
  rows?.[0]?.no &&
  (typeof rows[0].no === 'string' || typeof rows[0].no === 'number');

 //  const rowsDecap = lowercaseKeys(rows);

 const hasUniqueId = hasId || hasNo;
 return Array.isArray(rows)
  ? rows?.map((row: any, rowIndex: any) => ({
     ...row,
     id: hasUniqueId ? row.id || row.no : rowIndex,
     rowIndex,
    }))
  : [];
};

export const operatorMap = (operator: string) => {
 const mappings = {
  contains: 'contains',
  doesNotContain: 'doesnotcontain',
  equals: 'eq',
  doesNotEqual: 'neq',
  startsWith: 'startswith',
  endsWith: 'endswith',
  isEmpty: 'eq',
  isNotEmpty: 'neq',
  '=': 'eq',
  '!=': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
  is: 'eq',
  not: 'neq',
  after: 'gt',
  before: 'lt',
  onOrAfter: 'gte',
  onOrBefore: 'lte',
  isAnyOf: 'contains',
  modelEqual: 'eq',
  modelNotEqual: 'neq',
 };
 return mappings[operator as keyof typeof mappings] || 'wtf';
 // 'isAnyOf':
};

interface FilterWithLogicType {
 field: string;
 operator: string;
 value: any;
 logic?: 'Or' | 'And';
} // Adjust the import path as necessary

const addLogicToFilters = ({
 filters,
 structure,
}: {
 filters: FilterWithLogicType[];
 structure: StructureType[];
}) => {
 if (filters.length <= 1) return filters;
 let returnObj = [] as FilterWithLogicType[];

 const reduced = filters.reduce<Record<string, FilterWithLogicType[]>>(
  (acc, condition) => {
   if (!acc[condition.field]) {
    acc[condition.field] = [];
   }

   acc[condition.field].push(condition);

   return acc;
  },
  {},
 );
 let i = 0;
 Object.entries(reduced).forEach(([key, value]) => {
  const filterStructure = findStructure(structure, key);
  // console.log('filterStructure', key, filterStructure);
  if (value.length > 1) {
   returnObj = [
    ...returnObj,
    ...value.map(
     (val, ii) =>
      //  if (i === 0) {
      ({
       ...val,
       ...(filterStructure?.Type === 'datetime' && {
        operator: ii === 0 ? 'gte' : 'lte',
       }),
       logic:
        filterStructure?.Type === 'datetime'
         ? ('And' as const)
         : ('Or' as const),
      }),
     //  }
     //  return val;
    ),
   ];
  } else {
   if (returnObj[i]) {
    returnObj[i].logic = 'And';
   }
   returnObj.push({ ...value[0], logic: 'And' });
  }
  // console.log(key, value);
  // eslint-disable-next-line no-plusplus
  i++;
 });

 delete returnObj[returnObj.length - 1].logic;
 return returnObj;
};

const getColumnName = (
 field: string,
 sortStructure: StructureType | null,
): string => {
 if (!sortStructure) {
  return field;
 }

 if (isJson(sortStructure.ForeignKeyDefaultFilter)) {
  const filter = JSON.parse(
   sortStructure.ForeignKeyDefaultFilter,
  ) as { fields: string };
  const fieldsArray = filter.fields.split(',');
  if (fieldsArray.length > 1) {
   return `${field}.${fieldsArray[1]}`;
  }
 }

 if (sortStructure.InnerStructure) {
  return `${field}.Name`;
 }

 return field;
};

export const prepareSort = (
 sortModel: GridSortModel,
 structure: StructureType[],
): Array<{ field: string; dir: string }> | null => {
 if (!sortModel.length) {
  return null;
 }

 return sortModel.map((sortItem: GridSortModel[number]) => {
  const sortStructure = findStructure(structure, sortItem.field);
  const colName = getColumnName(sortItem.field, sortStructure);

  return {
   field: colName,
   dir: sortItem.sort || 'asc',
  };
 });
};

export const handleFilterModelChange = (
 model: GridFilterModel,
 details: GridCallbackDetails<'filter'>,
 setFilter: Dispatch<SetStateAction<GridFilterModel | undefined>>,
) => {
 if (typeof details.reason === 'undefined') return;

 console.log('onFilterModelChange', model, details);

 if (model.items.length > 0) {
  const newFiltersItems = model.items.map((item) => {
   const isArray = Array.isArray(item.value);
   let newValue = null;

   if (isArray) {
    const hasIds = item.value.some((i: any) => i.Id);
    newValue = hasIds ? item.value.map((i: any) => i.Id) : item.value;
   } else if (item?.value instanceof Date) {
    newValue = dayjs(item.value).format('YYYY-MM-DDTHH:mm:ss.SSS');
   } else {
    newValue = item.value;
   }
   return {
    id: item.id || item.field,
    field: item.field,
    operator: isArray ? 'isAnyOf' : item.operator,
    //  logic: model.logicOperator as string,
    value: newValue,
   };
  });
  // newFiltersItems[0].logic = 'And';
  //   console.log('onFilterModelChange 1', newFiltersItems);
  setFilter({
   items: newFiltersItems || [],
  });
 } else {
  console.log('elllllze');
  setFilter(model);
 }
};

export const prepareFilters = (
 filters: GridFilterItem[],
 structure: StructureType[],
) => {
 if (!filters.length || !structure.length) return null;

 const filtersMap: Array<FilterWithLogicType> = [];

 filters.forEach((filter: GridFilterItem) => {
  const currentStructure = findStructure(structure, filter.field);

  if (['model', 'array', 'enum'].includes(currentStructure?.Type)) {
   filtersMap.push({
    field:
     currentStructure?.Type === 'enum' ? filter.field : `${filter.field}.Id`,
    operator: 'eq',
    value: filter?.value?.Id ? filter.value.Id : filter.value,
   });
  } else {
   filtersMap.push({
    field: filter?.value?.Id ? `${filter.field}.Id` : filter.field,
    operator: currentStructure?.Type === 'string' ? 'contains' : 'eq', // operatorMap(filter.operator)
    value: filter?.value?.Id || filter?.value,
   });
  }
 });

 //  delete returnObj[returnObj.length - 1].logic;

 if (filtersMap.length > 0) {
  return {
   filters: addLogicToFilters({ filters: filtersMap, structure }),
  };
 }
 return null;
};
